import React, { useMemo } from "react";
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import FileManager from "./components/FileManager.tsx";
import { getMyPaletteColor } from "./utils/colorUtils.ts";
import { useDarkMode } from "./hooks/useDarkMode.ts";
import { useAuth } from "./hooks/useAuth.ts";
import { PaletteColorDarkHex, PaletteColorHex } from "./types/PaletteColor.ts";

// URLにmode=darkが含まれる場合、初期レンダリング前にdarkクラスを付与
const _initialParams = new URLSearchParams(window.location.search);
if (_initialParams.get("mode") === "dark") {
  document.documentElement.classList.add("dark");
}

const App = () => {
  const isDarkMode = useDarkMode(); // フックを使用
  const params = new URLSearchParams(window.location.search);
  const hpcode = parseInt(params.get("hpcode") || "0");
  const colorParam = params.get("color");
  const roomId = parseInt(params.get("roomid") || "0");
  const honbuId = parseInt(params.get("honbuid") || "0");

  // CheckController による認証チェック
  const { isAuthenticated, isLoading: isAuthLoading, error: authError } = useAuth(honbuId, roomId);

  const myPaletteColor = useMemo(
    () => getMyPaletteColor(colorParam),
    [colorParam],
  );

  const theme = useMemo(() => {
    // ダークモード時は明るめのアクセントカラー、ライトモード時は標準のアクセントカラー
    const primaryColor = isDarkMode
      ? PaletteColorDarkHex[myPaletteColor]
      : PaletteColorHex[myPaletteColor];

    return createTheme({
      palette: {
        mode: isDarkMode ? "dark" : "light", // ここが最重要！
        primary: {
          main: primaryColor,
        },
        background: {
          default: isDarkMode ? "#111827" : "#f8f9fa",
          paper: isDarkMode ? "#1f2937" : "#ffffff",
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              borderRadius: 8,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 12,
            },
          },
        },
      },
    });
  }, [myPaletteColor, isDarkMode]);

  // 認証チェック中のローディング画面
  if (isAuthLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // 認証失敗時のエラー画面
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Typography color="error">
            {authError || "アクセスが拒否されました。"}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FileManager hpcode={hpcode} roomId={roomId} honbuId={honbuId} />
    </ThemeProvider>
  );
};

export default App;
